import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { ApiError } from '../services/api';

// Types for global state
export interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'coach' | 'player';
  avatar?: string;
  sports: string[];
  isVerified: boolean;
  createdAt: string;
}

export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionUrl?: string;
}

export interface AppState {
  // User state
  user: User | null;
  isAuthenticated: boolean;
  
  // UI state
  loading: {
    global: boolean;
    auth: boolean;
    posts: boolean;
    communities: boolean;
    tournaments: boolean;
    achievements: boolean;
    chats: boolean;
    users: boolean;
  };
  
  // Error state
  errors: {
    global: ApiError | null;
    auth: ApiError | null;
    posts: ApiError | null;
    communities: ApiError | null;
    tournaments: ApiError | null;
    achievements: ApiError | null;
    chats: ApiError | null;
    users: ApiError | null;
  };
  
  // Notifications
  notifications: Notification[];
  unreadCount: number;
  
  // App settings
  settings: {
    theme: 'light' | 'dark';
    language: string;
    notifications: {
      email: boolean;
      push: boolean;
      inApp: boolean;
    };
  };
  
  // Network state
  isOnline: boolean;
  lastSync: string | null;
  
  // Cache state
  cache: {
    posts: any[];
    communities: any[];
    tournaments: any[];
    users: any[];
  };
}

// Action types
export type AppAction =
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_AUTHENTICATED'; payload: boolean }
  | { type: 'SET_LOADING'; payload: { key: keyof AppState['loading']; value: boolean } }
  | { type: 'SET_ERROR'; payload: { key: keyof AppState['errors']; value: ApiError | null } }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'MARK_NOTIFICATION_READ'; payload: string }
  | { type: 'CLEAR_NOTIFICATIONS' }
  | { type: 'UPDATE_SETTINGS'; payload: Partial<AppState['settings']> }
  | { type: 'SET_ONLINE'; payload: boolean }
  | { type: 'SET_LAST_SYNC'; payload: string }
  | { type: 'UPDATE_CACHE'; payload: { key: keyof AppState['cache']; value: any[] } }
  | { type: 'CLEAR_CACHE' }
  | { type: 'RESET_STATE' };

// Initial state
const initialState: AppState = {
  user: null,
  isAuthenticated: false,
  loading: {
    global: false,
    auth: false,
    posts: false,
    communities: false,
    tournaments: false,
    achievements: false,
    chats: false,
    users: false,
  },
  errors: {
    global: null,
    auth: null,
    posts: null,
    communities: null,
    tournaments: null,
    achievements: null,
    chats: null,
    users: null,
  },
  notifications: [],
  unreadCount: 0,
  settings: {
    theme: 'light',
    language: 'en',
    notifications: {
      email: true,
      push: true,
      inApp: true,
    },
  },
  isOnline: navigator.onLine,
  lastSync: null,
  cache: {
    posts: [],
    communities: [],
    tournaments: [],
    users: [],
  },
};

// Reducer function
function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
      };

    case 'SET_AUTHENTICATED':
      return {
        ...state,
        isAuthenticated: action.payload,
        user: action.payload ? state.user : null,
      };

    case 'SET_LOADING':
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.key]: action.payload.value,
        },
      };

    case 'SET_ERROR':
      return {
        ...state,
        errors: {
          ...state.errors,
          [action.payload.key]: action.payload.value,
        },
      };

    case 'ADD_NOTIFICATION':
      const newNotifications = [action.payload, ...state.notifications];
      return {
        ...state,
        notifications: newNotifications,
        unreadCount: newNotifications.filter(n => !n.read).length,
      };

    case 'REMOVE_NOTIFICATION':
      const filteredNotifications = state.notifications.filter(n => n.id !== action.payload);
      return {
        ...state,
        notifications: filteredNotifications,
        unreadCount: filteredNotifications.filter(n => !n.read).length,
      };

    case 'MARK_NOTIFICATION_READ':
      const updatedNotifications = state.notifications.map(n =>
        n.id === action.payload ? { ...n, read: true } : n
      );
      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: updatedNotifications.filter(n => !n.read).length,
      };

    case 'CLEAR_NOTIFICATIONS':
      return {
        ...state,
        notifications: [],
        unreadCount: 0,
      };

    case 'UPDATE_SETTINGS':
      return {
        ...state,
        settings: {
          ...state.settings,
          ...action.payload,
        },
      };

    case 'SET_ONLINE':
      return {
        ...state,
        isOnline: action.payload,
      };

    case 'SET_LAST_SYNC':
      return {
        ...state,
        lastSync: action.payload,
      };

    case 'UPDATE_CACHE':
      return {
        ...state,
        cache: {
          ...state.cache,
          [action.payload.key]: action.payload.value,
        },
      };

    case 'CLEAR_CACHE':
      return {
        ...state,
        cache: {
          posts: [],
          communities: [],
          tournaments: [],
          users: [],
        },
      };

    case 'RESET_STATE':
      return {
        ...initialState,
        settings: state.settings, // Preserve settings
      };

    default:
      return state;
  }
}

// Context
const AppContext = createContext<{
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
} | null>(null);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Initialize state from localStorage
  useEffect(() => {
    try {
      // Load user from localStorage
      const savedUser = localStorage.getItem('user');
      const savedToken = localStorage.getItem('token');
      
      if (savedUser && savedToken) {
        const user = JSON.parse(savedUser);
        dispatch({ type: 'SET_USER', payload: user });
      }

      // Load settings from localStorage
      const savedSettings = localStorage.getItem('appSettings');
      if (savedSettings) {
        const settings = JSON.parse(savedSettings);
        dispatch({ type: 'UPDATE_SETTINGS', payload: settings });
      }

      // Load notifications from localStorage
      const savedNotifications = localStorage.getItem('notifications');
      if (savedNotifications) {
        const notifications = JSON.parse(savedNotifications);
        notifications.forEach((notification: Notification) => {
          dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
        });
      }
    } catch (error) {
      console.error('Error loading state from localStorage:', error);
    }
  }, []);

  // Save state to localStorage when it changes
  useEffect(() => {
    try {
      if (state.user) {
        localStorage.setItem('user', JSON.stringify(state.user));
      } else {
        localStorage.removeItem('user');
      }
    } catch (error) {
      console.error('Error saving user to localStorage:', error);
    }
  }, [state.user]);

  useEffect(() => {
    try {
      localStorage.setItem('appSettings', JSON.stringify(state.settings));
    } catch (error) {
      console.error('Error saving settings to localStorage:', error);
    }
  }, [state.settings]);

  useEffect(() => {
    try {
      localStorage.setItem('notifications', JSON.stringify(state.notifications));
    } catch (error) {
      console.error('Error saving notifications to localStorage:', error);
    }
  }, [state.notifications]);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => dispatch({ type: 'SET_ONLINE', payload: true });
    const handleOffline = () => dispatch({ type: 'SET_ONLINE', payload: false });

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Auto-sync when coming back online
  useEffect(() => {
    if (state.isOnline && state.lastSync) {
      const lastSyncTime = new Date(state.lastSync).getTime();
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      if (now - lastSyncTime > fiveMinutes) {
        // Trigger sync if last sync was more than 5 minutes ago
        dispatch({ type: 'SET_LAST_SYNC', payload: new Date().toISOString() });
      }
    }
  }, [state.isOnline, state.lastSync]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the app context
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

// Helper hooks for specific parts of the state
export const useAuth = () => {
  const { state, dispatch } = useApp();
  return {
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading.auth,
    error: state.errors.auth,
    setUser: (user: User | null) => dispatch({ type: 'SET_USER', payload: user }),
    setLoading: (loading: boolean) => dispatch({ type: 'SET_LOADING', payload: { key: 'auth', value: loading } }),
    setError: (error: ApiError | null) => dispatch({ type: 'SET_ERROR', payload: { key: 'auth', value: error } }),
    logout: () => {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      dispatch({ type: 'RESET_STATE' });
    },
  };
};

export const useNotifications = () => {
  const { state, dispatch } = useApp();
  return {
    notifications: state.notifications,
    unreadCount: state.unreadCount,
    addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => {
      const newNotification: Notification = {
        ...notification,
        id: Math.random().toString(36).substr(2, 9),
        timestamp: new Date().toISOString(),
      };
      dispatch({ type: 'ADD_NOTIFICATION', payload: newNotification });
    },
    removeNotification: (id: string) => dispatch({ type: 'REMOVE_NOTIFICATION', payload: id }),
    markAsRead: (id: string) => dispatch({ type: 'MARK_NOTIFICATION_READ', payload: id }),
    clearAll: () => dispatch({ type: 'CLEAR_NOTIFICATIONS' }),
  };
};

export const useSettings = () => {
  const { state, dispatch } = useApp();
  return {
    settings: state.settings,
    updateSettings: (settings: Partial<AppState['settings']>) => 
      dispatch({ type: 'UPDATE_SETTINGS', payload: settings }),
  };
};

export const useNetwork = () => {
  const { state, dispatch } = useApp();
  return {
    isOnline: state.isOnline,
    lastSync: state.lastSync,
    setLastSync: (timestamp: string) => dispatch({ type: 'SET_LAST_SYNC', payload: timestamp }),
  };
};

export const useCache = () => {
  const { state, dispatch } = useApp();
  return {
    cache: state.cache,
    updateCache: (key: keyof AppState['cache'], value: any[]) => 
      dispatch({ type: 'UPDATE_CACHE', payload: { key, value } }),
    clearCache: () => dispatch({ type: 'CLEAR_CACHE' }),
  };
};